import { Fragment, type ReactNode } from "react";
import {
  BaseFeeTiming,
  BillingPlanStatus,
  BillingPricingMode,
  OrganizationBillingCategoryCode,
} from "@api/shared-runtime/enums";
import { ChevronDown } from "lucide-react";
import { Temporal } from "temporal-polyfill";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { EndBillingPlanDialog } from "./end-billing-plan-dialog";
import { PlanInvoicingDialog } from "./plan-invoicing-dialog";
import {
  BASE_FEE_TIMING_LABELS,
  BILLING_TIMEZONE,
  formatPlanCurrency,
  formatPlanDate,
  formatPlanInstant,
  getLastServiceDate,
  FREE_CATEGORY_LABEL,
  PRICING_UNAVAILABLE_LABEL,
  getSelectedCategoryRate,
  isFreeCategoryRate,
  type BillingPlanCategory,
  type BillingPlanData,
  type BillingPlanVersion,
} from "./billing-plan-values";
import { PlanWagePassThroughDialog } from "./plan-wage-pass-through-dialog";
import {
  WAGE_PASS_THROUGH_LABEL,
  WAGE_PASS_THROUGH_OFF_LABEL,
  WAGE_PASS_THROUGH_PLAN_CARD_ON,
} from "./wage-pass-through-copy";

type PlanSection = {
  status: BillingPlanStatus;
  title: string;
  label: string;
  badgeVariant: BadgeProps["variant"];
};

const CURRENT_SECTION: PlanSection = {
  status: BillingPlanStatus.Active,
  title: "Current plan",
  label: "Active",
  badgeVariant: "default",
};
const SCHEDULED_SECTION: PlanSection = {
  status: BillingPlanStatus.Scheduled,
  title: "Scheduled plan",
  label: "Upcoming",
  badgeVariant: "secondary",
};
const ENDED_SECTION: PlanSection = {
  status: BillingPlanStatus.Ended,
  title: "Plan history",
  label: "Ended",
  badgeVariant: "outline",
};

/** Wiring for the per-plan actions; absent on read-only renders. */
export type PlanActions = {
  onSaved: () => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  onReload: () => void;
  busy: boolean;
};

export function BillingPlanHistory({
  data,
  actions,
}: {
  data: BillingPlanData;
  actions?: PlanActions;
}) {
  if (data.plans.length === 0) {
    return (
      <p className="rounded-lg border p-4 text-sm text-muted-foreground">
        No plan is configured for this business unit.
      </p>
    );
  }
  const plansFor = (section: PlanSection) =>
    data.plans.filter((plan) => plan.status === section.status);
  const endedPlans = plansFor(ENDED_SECTION);
  return (
    <div className="space-y-6">
      {[CURRENT_SECTION, SCHEDULED_SECTION].map((section) => {
        const plans = plansFor(section);
        if (plans.length === 0) {
          return null;
        }
        return (
          <section
            key={section.status}
            className="space-y-2"
            aria-label={section.title}
          >
            <h3 className="text-sm font-semibold">{section.title}</h3>
            {plans.map((plan) => (
              <PlanTerms
                key={plan.id}
                plan={plan}
                data={data}
                section={section}
                actions={actions}
              />
            ))}
          </section>
        );
      })}
      {endedPlans.length > 0 && (
        <PlanHistoryList plans={endedPlans} data={data} />
      )}
    </div>
  );
}

// Ended plans are evidence of past terms. Invoicing and end-date actions only
// apply to the plan in force, so history entries render without actions.
function PlanHistoryList({
  plans,
  data,
}: {
  plans: BillingPlanVersion[];
  data: BillingPlanData;
}) {
  return (
    <section className="space-y-2" aria-label={ENDED_SECTION.title}>
      <h3 className="text-sm font-semibold">
        {ENDED_SECTION.title} ({plans.length})
      </h3>
      <Accordion type="single" collapsible className="rounded-lg border">
        {plans.map((plan) => (
          <AccordionItem
            key={plan.id}
            value={plan.id}
            className="px-4 last:border-b-0"
          >
            <AccordionTrigger className="gap-3 py-3 text-left text-sm font-normal hover:no-underline">
              <span className="flex flex-1 flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <span>{formatServicePeriod(plan)}</span>
                <span className="font-medium">
                  {formatPlanCurrency(plan.baseMonthlyAmount)} / month
                </span>
              </span>
              <ChevronDown
                className="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionTrigger>
            <AccordionContent>
              <PlanTerms
                plan={plan}
                data={data}
                section={ENDED_SECTION}
                compact
              />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function formatServicePeriod(plan: BillingPlanVersion): string {
  const start = formatPlanInstant(plan.effectiveStartsAt);
  if (!plan.effectiveEndsAt) {
    return start;
  }
  return `${start} – ${formatPlanDate(getLastServiceDate(plan.effectiveEndsAt))}`;
}

function PlanTerms({
  plan,
  data,
  section,
  actions,
  compact = false,
}: {
  plan: BillingPlanVersion;
  data: BillingPlanData;
  section: PlanSection;
  actions?: PlanActions;
  /** Omits the status and price row when the surrounding list already shows them. */
  compact?: boolean;
}) {
  const isCurrent = plan.status === BillingPlanStatus.Active;
  const isScheduled = plan.status === BillingPlanStatus.Scheduled;
  return (
    <div
      className={cn(
        "space-y-4 text-sm",
        !compact && "rounded-lg border p-4",
        isCurrent && "border-slate-300 shadow-xs",
      )}
    >
      {!compact && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Badge variant={section.badgeVariant} size="small">
            <span
              aria-hidden="true"
              className="mr-1.5 inline-block size-1.5 rounded-full bg-current"
            />
            {section.label}
          </Badge>
          <span
            className={cn("font-medium", isCurrent && "text-lg font-semibold")}
          >
            {formatPlanCurrency(plan.baseMonthlyAmount)} / month
          </span>
        </div>
      )}
      <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        <PlanFact label="Starts">
          {formatPlanInstant(plan.effectiveStartsAt)}
          {isScheduled && (
            <span className="text-muted-foreground">
              {" "}
              ({formatDaysUntil({ startsAt: plan.effectiveStartsAt, data })})
            </span>
          )}
        </PlanFact>
        <PlanFact label="Renews">
          Day {plan.billingAnchorDay} of each month
        </PlanFact>
        <PlanFact label="Base fee">
          {BASE_FEE_TIMING_LABELS[plan.baseFeeTiming]}
        </PlanFact>
        <PlanFact label="Invoicing">
          {plan.invoicingStartsAt ? (
            `From ${formatPlanInstant(plan.invoicingStartsAt)}`
          ) : (
            <span className="text-amber-800">
              Not invoiced: usage is reported only
            </span>
          )}
        </PlanFact>
        {plan.effectiveEndsAt && (
          <PlanFact label="Service through">
            {formatPlanDate(getLastServiceDate(plan.effectiveEndsAt))}{" "}
            (inclusive, New York)
          </PlanFact>
        )}
      </dl>
      {isScheduled && (
        <ScheduledServicePeriod
          startsAt={plan.effectiveStartsAt}
          baseFeeTiming={plan.baseFeeTiming}
        />
      )}
      <CategoryPricingTable plan={plan} data={data} actions={actions} />
      {plan.notes && (
        <p className="whitespace-pre-wrap">
          <span className="mr-2 text-muted-foreground">Notes</span>
          <span className="italic">{plan.notes}</span>
        </p>
      )}
      {actions && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
          <PlanInvoicingDialog
            key={`invoicing-${plan.id}-${data.version}`}
            plan={plan}
            data={data}
            onSaved={actions.onSaved}
            onBusyChange={actions.onBusyChange}
            onReload={actions.onReload}
            busy={actions.busy}
          />
          {isCurrent && data.canEnd && (
            <EndBillingPlanDialog
              key={`end-${data.version}`}
              data={data}
              onSaved={actions.onSaved}
              onBusyChange={actions.onBusyChange}
              onReload={actions.onReload}
              busy={actions.busy}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PlanFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="shrink-0 text-muted-foreground sm:w-28">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function formatDaysUntil({
  startsAt,
  data,
}: {
  startsAt: string;
  data: BillingPlanData;
}): string {
  const startDate = Temporal.Instant.from(startsAt)
    .toZonedDateTimeISO(BILLING_TIMEZONE)
    .toPlainDate();
  const days = Temporal.PlainDate.from(data.today).until(startDate).days;
  if (days <= 0) {
    return "starts today";
  }
  if (days === 1) {
    return "starts tomorrow";
  }
  return `starts in ${days} days`;
}

function CategoryPricingTable({
  plan,
  data,
  actions,
}: {
  plan: BillingPlanVersion;
  data: BillingPlanData;
  actions?: PlanActions;
}) {
  const isArchived = Boolean(
    data.unit.deletedAt || data.unit.organizationDeletedAt,
  );
  const canChangeWages =
    actions !== undefined &&
    !isArchived &&
    plan.status !== BillingPlanStatus.Ended;
  return (
    <table className="w-full border-collapse text-sm">
      <caption className="sr-only">Category pricing</caption>
      <tbody>
        {data.categories.map((category) => {
          const price = plan.prices.find(
            (item) => item.billingCategoryId === category.categoryCode,
          );
          return (
            <Fragment key={category.categoryCode}>
              <tr className="border-t">
                <th
                  scope="row"
                  className="w-1/3 py-1.5 pr-4 text-left font-normal text-muted-foreground"
                >
                  {category.displayName}
                </th>
                <td className="py-1.5">
                  {getHistoricalPriceLabel({ price, category })}
                </td>
              </tr>
              {category.categoryCode ===
                OrganizationBillingCategoryCode.ShiftMarkup && (
                <tr className="border-t">
                  <th
                    scope="row"
                    className="w-1/3 py-1.5 pr-4 text-left font-normal text-muted-foreground"
                  >
                    {WAGE_PASS_THROUGH_LABEL}
                  </th>
                  <td className="py-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className={cn(
                          !plan.wagePassThroughEnabled &&
                            "text-muted-foreground",
                        )}
                      >
                        {plan.wagePassThroughEnabled
                          ? WAGE_PASS_THROUGH_PLAN_CARD_ON
                          : WAGE_PASS_THROUGH_OFF_LABEL}
                      </span>
                      {canChangeWages && actions && (
                        <PlanWagePassThroughDialog
                          key={`wages-${plan.id}-${data.version}`}
                          plan={plan}
                          data={data}
                          onSaved={actions.onSaved}
                          onBusyChange={actions.onBusyChange}
                          onReload={actions.onReload}
                          busy={actions.busy}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function getHistoricalPriceLabel({
  price,
  category,
}: {
  price: BillingPlanVersion["prices"][number] | undefined;
  category: BillingPlanCategory;
}): string {
  // Existing terms remain visible even when a category can no longer receive
  // new pricing. Catalog support controls editing, not historical evidence.
  if (!price) {
    return category.supported ? FREE_CATEGORY_LABEL : PRICING_UNAVAILABLE_LABEL;
  }
  if (isFreeCategoryRate(getSelectedCategoryRate(price))) {
    return FREE_CATEGORY_LABEL;
  }
  if (price.pricingMode === BillingPricingMode.PercentOfBasis) {
    return `${Number(price.basisPercent)}% of wages`;
  }
  return `${price.includedQuantity} included, ${formatPlanCurrency(price.unitAmount ?? "0")} per additional ${category.unitNoun}`;
}

function ScheduledServicePeriod({
  startsAt,
  baseFeeTiming,
}: {
  startsAt: string;
  baseFeeTiming: BaseFeeTiming;
}) {
  const firstDay = Temporal.Instant.from(startsAt)
    .toZonedDateTimeISO(BILLING_TIMEZONE)
    .toPlainDate();
  const nextMonth = firstDay.add({ months: 1 });
  const firstInvoice =
    baseFeeTiming === BaseFeeTiming.InAdvance
      ? firstDay
      : nextMonth.add({ days: 1 });
  return (
    <div className="space-y-1 text-muted-foreground">
      <p>
        First service period: {formatPlanDate(firstDay.toString())} –{" "}
        {formatPlanDate(nextMonth.subtract({ days: 1 }).toString())}
      </p>
      <p>
        Expected first invoice: {formatPlanDate(firstInvoice.toString())}, once
        invoicing is enabled.
      </p>
    </div>
  );
}
